import { useEffect, useRef, useState } from "react";
import api, { formatError } from "../../lib/api";
import { Brand } from "../../components/Brand";
import { scoreColor } from "../../lib/scoring";
import { Download, FileSpreadsheet, Printer, Loader2, ImagePlus, Save, Building2, X, Mail } from "lucide-react";
import { toast } from "sonner";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <div className="font-heading font-bold text-ink text-lg mb-3 border-b border-slate-200 pb-2">{title}</div>
    {children}
  </div>
);

export default function CollegeReports() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/college/analytics").then((r) => setD(r.data));
    api.get("/college/branding").then((r) => setBranding(r.data));
  }, []);
  if (!d || !branding) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const accent = branding.primary_color || "#0055FF";

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo must be under 500 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setBranding((b) => ({ ...b, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      await api.put("/college/branding", {
        logo: branding.logo || "", note: branding.note || "",
        primary_color: branding.primary_color || "#0055FF", placement_officer: branding.placement_officer || "",
      });
      toast.success("Report branding saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingBrand(false); }
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/college/report.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "mock2crack-college-report.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/college/email-report");
      toast.success(data.note || `Report emailed to ${data.to}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  const o = d.overview;
  const term = `${new Date().toLocaleString("en", { month: "long" })} ${new Date().getFullYear()}`;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">College Report</h1>
          <p className="text-ink-soft text-sm mt-1">Download or print your employability intelligence report. <span className="text-brand font-semibold">Auto-emailed to your placement cell on the 1st of every month.</span></p>
        </div>
        <div className="flex gap-2">
          <button data-testid="email-report" onClick={emailReport} disabled={emailing} className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors">{emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email Report</button>
          <button data-testid="export-csv" onClick={exportCsv} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export CSV</button>
          <button data-testid="download-pdf" onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient transition-colors"><Printer className="h-4 w-4" /> Download PDF</button>
        </div>
      </div>

      {/* Branding editor (not printed) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 mb-6 print:hidden" data-testid="branding-editor">
        <div className="flex items-center gap-2 mb-4"><Building2 className="h-4 w-4 text-brand" /><div className="font-heading font-bold text-ink">Report Branding</div></div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-faint">College Logo</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl border border-slate-200 grid place-items-center overflow-hidden bg-slate-50 shrink-0">
                {branding.logo ? <img src={branding.logo} alt="logo" className="h-full w-full object-contain" /> : <ImagePlus className="h-6 w-6 text-slate-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="logo-file" />
                <button onClick={() => fileRef.current?.click()} className="text-xs font-bold text-white bg-brand hover:bg-brand-hover px-3.5 py-2 rounded-full transition-colors">Upload Logo</button>
                {branding.logo && <button data-testid="logo-remove" onClick={() => setBranding({ ...branding, logo: "" })} className="text-xs font-semibold text-red-500 inline-flex items-center gap-1"><X className="h-3 w-3" /> Remove</button>}
              </div>
            </div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-faint mt-4 block">Logo URL (optional)</label>
            <input data-testid="logo-url" value={branding.logo && branding.logo.startsWith("http") ? branding.logo : ""} onChange={(e) => setBranding({ ...branding, logo: e.target.value })} placeholder="https://…/logo.png" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
            <div className="flex items-center gap-3 mt-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-faint">Accent</label>
                <input data-testid="brand-color" type="color" value={branding.primary_color || "#0055FF"} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="mt-1.5 h-10 w-16 rounded-lg border border-slate-300 cursor-pointer block" />
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-faint">Placement Officer</label>
                <input data-testid="placement-officer" value={branding.placement_officer || ""} onChange={(e) => setBranding({ ...branding, placement_officer: e.target.value })} placeholder="Name, Placement Cell" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-faint">Placement Cell Note</label>
            <textarea data-testid="placement-note" value={branding.note || ""} onChange={(e) => setBranding({ ...branding, note: e.target.value })} rows={6} placeholder="A message from the placement cell that appears on the report…" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
            <button data-testid="save-branding" onClick={saveBranding} disabled={savingBrand} className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full disabled:opacity-60 transition-colors">{savingBrand ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Branding</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-8" id="report">
        {/* Branded cover page */}
        <div className="text-center py-10 mb-8 rounded-2xl relative overflow-hidden" style={{ background: `linear-gradient(135deg, #0f172a, ${accent})`, pageBreakAfter: "always" }} data-testid="report-cover">
          <div className="absolute inset-0 bg-grid opacity-10" />
          <div className="relative">
            {branding.logo && <img src={branding.logo} alt="College logo" className="h-20 w-20 object-contain rounded-xl bg-white/90 p-1.5 mx-auto mb-5" />}
            <div className="text-white/70 text-xs font-bold uppercase tracking-[0.2em] mb-2">Employability Report</div>
            <div className="font-heading text-3xl sm:text-4xl font-extrabold text-white">{d.college?.name}</div>
            <div className="text-white/80 text-sm mt-1">{d.college?.location}</div>
            <div className="inline-block mt-4 text-white font-semibold text-sm px-4 py-1.5 rounded-full bg-white/15">{term}</div>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {[["Students", o.total_students], ["Assessed", o.assessed], ["Avg Readiness", o.avg_readiness], ["Interview Ready", `${o.ready_pct}%`]].map(([k, v]) => (
                <div key={k} className="bg-white/10 rounded-xl px-5 py-3 min-w-[110px]"><div className="num font-bold text-2xl text-white">{v}</div><div className="text-[11px] text-white/70">{k}</div></div>
              ))}
            </div>
            <div className="mt-8 text-white/60 text-xs">Powered by Mock2Crack · Generated {new Date().toISOString().slice(0, 10)}</div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6 pb-4 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-center gap-4">
            {branding.logo && <img src={branding.logo} alt="College logo" className="h-14 w-14 object-contain rounded-lg" data-testid="report-logo" />}
            <div>
              <div className="font-heading font-bold text-xl text-ink">{d.college?.name}</div>
              <div className="text-xs text-ink-faint">{d.college?.location} · Employability Report · {new Date().toISOString().slice(0, 10)}</div>
              {branding.placement_officer && <div className="text-xs text-ink-soft mt-0.5">{branding.placement_officer}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-ink-faint">Powered by</div>
            <Brand />
          </div>
        </div>

        {branding.note && (
          <div className="mb-6 rounded-xl p-4 border-l-4" style={{ borderColor: accent, background: accent + "10" }} data-testid="report-note">
            <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>Note from the Placement Cell</div>
            <p className="text-sm text-ink-soft whitespace-pre-line">{branding.note}</p>
          </div>
        )}

        <Section title="College Readiness Overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Total Students", o.total_students], ["Assessed", o.assessed], ["Avg Readiness", o.avg_readiness], ["Interview Ready", `${o.ready_pct}%`],
              ["Almost Ready", `${o.almost_pct}%`], ["Needs Improvement", `${o.needs_pct}%`], ["Not Assessed", `${o.not_assessed_pct}%`], ["Interviews", o.interviews_completed]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-200 p-3"><div className="num font-bold text-xl text-ink">{v}</div><div className="text-xs text-ink-faint">{k}</div></div>
            ))}
          </div>
        </Section>

        <Section title="Job Role Readiness">
          {d.job_role_readiness.map((r) => (
            <div key={r.name} className="flex items-center gap-3 mb-2">
              <span className="text-sm text-ink-soft w-40 shrink-0">{r.name}</span>
              <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.readiness}%`, background: scoreColor(r.readiness) }} /></div>
              <span className="num font-bold text-sm text-ink w-10 text-right">{r.readiness}%</span>
            </div>
          ))}
        </Section>

        <Section title="College-Wide Skill Gaps">
          {d.skill_intelligence.map((s) => (
            <div key={s.skill} className="flex items-center gap-3 mb-2">
              <span className="text-sm text-ink-soft w-40 shrink-0">{s.label}</span>
              <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-red-400" style={{ width: `${s.below_pct}%` }} /></div>
              <span className="num font-bold text-sm text-red-500 w-14 text-right">{s.below_pct}%</span>
            </div>
          ))}
        </Section>

        <Section title="Batch Comparison">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-bold text-ink-faint border-b border-slate-200"><th className="py-2">Batch</th><th className="py-2">Completion</th><th className="py-2">Avg Readiness</th><th className="py-2">Coding</th><th className="py-2">Comm.</th><th className="py-2">Technical</th></tr></thead>
            <tbody>{d.batch_comparison.map((b) => (
              <tr key={b.batch} className="border-b border-slate-100"><td className="py-2 num font-bold">{b.batch}</td><td className="py-2 num">{b.completion}%</td><td className="py-2 num">{b.avg_readiness}</td><td className="py-2 num">{b.coding}</td><td className="py-2 num">{b.communication}</td><td className="py-2 num">{b.technical}</td></tr>
            ))}</tbody>
          </table>
        </Section>

        <div className="grid sm:grid-cols-2 gap-8">
          <Section title="Top-Performing Students">
            {d.top_performers.map((s, i) => (
              <div key={s.id} className="flex justify-between text-sm py-1.5 border-b border-slate-100"><span className="text-ink">{i + 1}. {s.name} <span className="text-ink-faint">({s.branch})</span></span><span className="num font-bold" style={{ color: scoreColor(s.readiness) }}>{s.readiness}</span></div>
            ))}
          </Section>
          <Section title="Students Requiring Intervention">
            {d.interventions.slice(0, 8).map((s) => (
              <div key={s.id} className="text-sm py-1.5 border-b border-slate-100"><span className="font-semibold text-ink">{s.name}</span> <span className="text-xs text-red-600">— {s.reason}</span></div>
            ))}
          </Section>
        </div>

        <div className="text-center text-xs text-ink-faint mt-6 pt-4 border-t border-slate-200">{d.college?.name} · Prepared with Mock2Crack Employability Intelligence · © 2026</div>
      </div>
    </div>
  );
}
